import React from "react";
import { Link } from "@/components/common/Link";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { Reveal } from "@/components/cosmic/Reveal";
import { Globe, Atom, SlidersHorizontal, Compass, Cpu, Users, ArrowRight } from "lucide-react";
interface PortalItem {
  id: string;
  title: string;
  icon: typeof Globe;
  shortDesc: string;
  revealText: string;
  ctaText: string;
  accent: "cyan" | "blue" | "violet" | "white";
}
const PORTALS: PortalItem[] = [{
  id: "atlas",
  title: "Multiverse Atlas",
  icon: Globe,
  shortDesc: "Cartography of bubble cosmos, dimensional branes, and inflationary horizons.",
  revealText: "Journey through cosmic topology maps, inflationary scenarios, and mathematical cosmologies.",
  ctaText: "Open Atlas",
  accent: "cyan"
}, {
  id: "quantum-lab",
  title: "Quantum Lab",
  icon: Atom,
  shortDesc: "Interactive mechanics of superposition, entanglement, and probability fields.",
  revealText: "Calibrate wave-particle interference and observe simulated quantum state collapses.",
  ctaText: "Launch Lab",
  accent: "blue"
}, {
  id: "what-if",
  title: "What-If Universe",
  icon: SlidersHorizontal,
  shortDesc: "Alter fundamental physics constants and simulate alternative realities.",
  revealText: "Modify fine-structure constants, gravity, and dimensions to test habitability limits.",
  ctaText: "Run Simulation",
  accent: "violet"
}, {
  id: "discoveries",
  title: "Cosmic Discoveries",
  icon: Compass,
  shortDesc: "Dispatches, observational astronomy, and theoretical physics breakthroughs.",
  revealText: "Read rigorous science communication analyzing telescope data, gravitational waves, and papers.",
  ctaText: "Read Archives",
  accent: "white"
}, {
  id: "universe-creator",
  title: "Create Your Universe",
  icon: Cpu,
  shortDesc: "Architect custom cosmic configurations and generate collectible specs.",
  revealText: "Synthesize matter-antimatter ratios, dark energy levels, and share specimen links.",
  ctaText: "Create Cosmos",
  accent: "cyan"
}, {
  id: "infinite-selves",
  title: "Your Infinite Selves",
  icon: Users,
  shortDesc: "Fictional multiverse storytelling mapping divergent life trajectories.",
  revealText: "Explore imaginative branches of identity across parallel realities with rich narrative banks.",
  ctaText: "Explore Selves",
  accent: "violet"
}];
const accentGlowMap: Record<PortalItem["accent"], string> = {
  cyan: "hover:border-primary/60 hover:shadow-[0_0_28px_rgba(0,229,255,0.22)]",
  blue: "hover:border-blue-400/60 hover:shadow-[0_0_28px_rgba(59,130,246,0.22)]",
  violet: "hover:border-purple-400/60 hover:shadow-[0_0_28px_rgba(168,85,247,0.22)]",
  white: "hover:border-white/50 hover:shadow-[0_0_28px_rgba(255,255,255,0.18)]"
};
const accentIconMap: Record<PortalItem["accent"], string> = {
  cyan: "text-primary group-hover:drop-shadow-[0_0_8px_var(--glow-primary)]",
  blue: "text-blue-400 group-hover:drop-shadow-[0_0_8px_var(--glow-laser)]",
  violet: "text-purple-400 group-hover:drop-shadow-[0_0_8px_var(--glow-violet)]",
  white: "text-slate-100 group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
};
function PortalCardBody({
  portal,
  index
}: {
  portal: PortalItem;
  index: number;
}) {
  const Icon = portal.icon;
  return <>
      {/* Card top specular accent */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent group-hover:via-primary/50 transition-colors" />

      {/* Header & Icon */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="size-11 rounded-lg bg-card/80 border border-border/60 flex items-center justify-center backdrop-blur-xs">
            <Icon className={`size-5 transition-transform duration-300 group-hover:scale-110 ${accentIconMap[portal.accent]}`} />
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground/75">
            0{index + 1} // PORTAL
          </span>
        </div>

        <h3 className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-foreground mb-2 group-hover:text-primary transition-colors">
          {portal.title}
        </h3>

        <p className="font-sans text-sm text-muted-foreground leading-relaxed">
          {portal.shortDesc}
        </p>
      </div>

      {/* Hover/Focus Reveal block */}
      <div className="mt-4 pt-3 border-t border-border/40">
        <div className="ql-card-reveal space-y-3">
          <p className="font-sans text-xs text-muted-foreground/90 leading-relaxed">
            {portal.revealText}
          </p>
        </div>

        <div className="flex items-center justify-between pt-2 text-primary font-serif text-xs font-semibold uppercase tracking-wider min-h-[32px]">
          <span>{portal.ctaText}</span>
          <ArrowRight className="size-3.5 shrink-0 transition-transform duration-200 group-hover:translate-x-1" />
        </div>
      </div>
    </>;
}
export default function Portals() {
  return <section id="portals" data-nav="dark" className="relative py-16 sm:py-20 lg:py-28 w-full max-w-full overflow-x-clip text-foreground scroll-mt-12">
      <div className="relative z-10 max-w-[76rem] w-full mx-auto px-4 sm:px-6">
        <Reveal>
          <SectionHeading eyebrow="GATEWAYS TO EXPLORATION" title="Six Dimensions of Inquiry" description="Access our interactive cosmos: theoretical structures, quantum simulations, parametric constants, and multiverse narratives." align="center" />
        </Reveal>

        {/* 6 feature cards in a responsive grid: 1 col on mobile, 2 on tablet, 3 on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
          <Reveal delay={0} className="h-full">
            <Link to="/MultiverseAtlasPage" className={`ql-card group flex flex-col justify-between h-full p-6 sm:p-7 block transition-all duration-300 ${accentGlowMap[PORTALS[0].accent]}`}>
              <PortalCardBody portal={PORTALS[0]} index={0} />
            </Link>
          </Reveal>

          <Reveal delay={80} className="h-full">
            <Link to="/QuantumLabPage" className={`ql-card group flex flex-col justify-between h-full p-6 sm:p-7 block transition-all duration-300 ${accentGlowMap[PORTALS[1].accent]}`}>
              <PortalCardBody portal={PORTALS[1]} index={1} />
            </Link>
          </Reveal>

          <Reveal delay={160} className="h-full">
            <Link to="/WhatIfUniversePage" className={`ql-card group flex flex-col justify-between h-full p-6 sm:p-7 block transition-all duration-300 ${accentGlowMap[PORTALS[2].accent]}`}>
              <PortalCardBody portal={PORTALS[2]} index={2} />
            </Link>
          </Reveal>

          <Reveal delay={240} className="h-full">
            <Link to="/CosmicDiscoveriesPage" className={`ql-card group flex flex-col justify-between h-full p-6 sm:p-7 block transition-all duration-300 ${accentGlowMap[PORTALS[3].accent]}`}>
              <PortalCardBody portal={PORTALS[3]} index={3} />
            </Link>
          </Reveal>

          <Reveal delay={320} className="h-full">
            <Link to="/CreateYourUniversePage" className={`ql-card group flex flex-col justify-between h-full p-6 sm:p-7 block transition-all duration-300 ${accentGlowMap[PORTALS[4].accent]}`}>
              <PortalCardBody portal={PORTALS[4]} index={4} />
            </Link>
          </Reveal>

          <Reveal delay={400} className="h-full">
            <Link to="/YourInfiniteSelvesPage" className={`ql-card group flex flex-col justify-between h-full p-6 sm:p-7 block transition-all duration-300 ${accentGlowMap[PORTALS[5].accent]}`}>
              <PortalCardBody portal={PORTALS[5]} index={5} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>;
}